// ── AR Flow — NOVA INTERFACE, Etapa 7: Meu Dia ────────────────────────────────
// A nova ⚡ Central: só as PRIORIDADES do dia, nesta ordem (decisão da Ana):
//   1. Responder quem está aguardando (sempre a prioridade nº 1);
//   2. Prospectar hoje (fila do tombamento);
//   3. Retornos de hoje e atrasados.
// Meta, "Você poderia estar com" e frase motivadora ficam no Dashboard.
// Só visual: recebe os dados já calculados pela ⚡ Central e chama as mesmas
// funções (abrir conversa na aba da origem, concluir lembrete).

import TextSendStatus, { useTextSendPriority } from "../TextSendStatus";
import ReminderCompletionButton from "../ReminderCompletionButton";
import BindingDeadlineNotice, { bindingDeadlineIsActive } from "../BindingDeadlineNotice";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";

function Bloco({ theme, titulo, contador, cor, children, acao }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  return (
    <section style={{ background: "#fff", border: `1px solid ${line}`, borderTop: `3px solid ${cor}`, borderRadius: 4, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${line}` }}>
        <h2 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 17, color: th.text }}>{titulo}</h2>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          {acao}
          <span style={{ fontFamily: MONO, fontSize: 14, color: cor }}>{contador}</span>
        </div>
      </div>
      <div>{children}</div>
    </section>
  );
}

function Vazio({ theme, texto }) {
  return <div style={{ padding: "14px 16px", fontSize: 14, color: theme.muted }}>{texto}</div>;
}

export default function MeuDia({ theme, hoje, nomeUsuario, currentUser, mostrarConsultor, responder, alerta15h = [], retornos, fila, entregues = [], entreguesErro = "", onAbrirPosVenda, onAbrirResponder, onAbrirRetorno, onConcluirRetorno, onIrProspeccao, bindingSales = [], bindingError = "", onOpenBindingSale }) {
  const orderedResponder = useTextSendPriority(responder);
  const orderedRetornos = useTextSendPriority(retornos, r => ({ storeId:r.storeId, phone:r.clientPhone || r.phone }));
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const linha = { width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${line}`, padding: "11px 16px", cursor: "pointer", fontFamily: "inherit", color: th.text, display: "flex", gap: 12, alignItems: "center" };
  const filaItens = fila?.fila?.itens || [];
  const filaFeitos = filaItens.filter(i => i.status === "feito").length;
  const filaPendentes = filaItens.filter(i => i.status !== "feito");
  const mostrarBloqueioData = fila?.fila?.naoIniciado;
  const pedidosEntregues = currentUser?.role === "consultant"
    && Number.isInteger(Number(currentUser.id)) && Number(currentUser.id) > 0
    && Number.isInteger(Number(currentUser.storeId)) && Number(currentUser.storeId) > 0
    ? entregues.filter(item => {
      const consultantId = Number(item?.consultantId ?? item?.responsavelId ?? item?.consultorId ?? item?.vendidoPor?.consultantId);
      const storeId = Number(item?.storeId);
      const pedido = item?.pedido ?? item?.liftPedidoId;
      return consultantId === Number(currentUser.id) && storeId === Number(currentUser.storeId)
        && pedido != null;
    }) : [];
  const bindingPendencies = (bindingSales || []).filter(sale =>
    sale?.bindingDeadline?.eligible
    && ["pending", "overdue"].includes(sale?.bindingDeadline?.state)
    && bindingDeadlineIsActive()
    && (currentUser?.role !== "consultant"
      || Number(sale.vendidoPor?.consultantId ?? sale.responsavelId) === Number(currentUser.id))
  );

  return (
    <div style={{ maxWidth: 880 }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 700, color: th.text }}>Meu Dia</div>
        <div style={{ fontSize: 14, color: th.muted }}>{nomeUsuario ? `${nomeUsuario}, ` : ""}suas prioridades de {hoje}. Comece sempre por quem está esperando.</div>
      </div>

      {entreguesErro && <div role="alert" style={{ color:"#991B1B", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:4, padding:"9px 12px", marginBottom:14 }}>
        Não foi possível carregar os avisos de entrega: {entreguesErro}
      </div>}
      {bindingDeadlineIsActive() && (bindingPendencies.length > 0 || bindingError) && (
        <Bloco theme={th} titulo="Vincular vendas LIFT" contador={bindingPendencies.length} cor="#B42318">
          {bindingError && <div role="alert" style={{ padding: "10px 16px", color: "#991B1B" }}>
            Não foi possível carregar os prazos de vinculação: {bindingError}
          </div>}
          {!bindingError && bindingPendencies.map(sale => (
            <div key={`${sale.storeId}-${sale.liftPedidoId}`} style={{ padding: "0 16px 10px" }}>
              <BindingDeadlineNotice sale={sale} onOpen={onOpenBindingSale} />
            </div>
          ))}
        </Bloco>
      )}
      {pedidosEntregues.length > 0 && (
        <Bloco theme={th} titulo="Pós-venda de hoje" contador={pedidosEntregues.length} cor="#1E6B4A">
          {pedidosEntregues.map(item => {
            const pedido = item.pedido ?? item.liftPedidoId;
            const aviso = `📦 Pedido #${pedido} entregue: fazer pós-venda`;
            return <button key={`${item.storeId}-${pedido}`} type="button"
              onClick={() => onAbrirPosVenda?.(item)} style={{ ...linha, fontWeight:700 }}>
              <span aria-hidden="true">📦</span>
              <span>{aviso}</span>
            </button>;
          })}
        </Bloco>
      )}

      {alerta15h.length > 0 && (
        <Bloco theme={th} titulo="🚨 Sem resposta há mais de 15h" contador={alerta15h.length} cor="#7A1010">
          {alerta15h.map(item => (
            <button key={`alerta-${item.key}`} type="button" onClick={() => onAbrirResponder(item)} style={linha}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 700, fontSize: 15 }}>{item.name}</span>
                <span style={{ display: "block", fontSize: 13, color: th.muted, marginTop: 2 }}>{[item.consultor, item.origin].filter(Boolean).join(" · ")}</span>
              </span>
              {item.ago && <span style={{ fontFamily: MONO, fontSize: 12, color: "#7A1010", flexShrink: 0 }}>há {item.ago}</span>}
            </button>
          ))}
        </Bloco>
      )}

      <Bloco theme={th} titulo="Responder agora" contador={responder.length} cor="#B42318">
        {responder.length === 0 && <Vazio theme={th} texto="Ninguém esperando resposta. 👏" />}
        {orderedResponder.map(item => (
          <button key={item.key} type="button" onClick={() => onAbrirResponder(item)} style={linha}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: "#B42318", flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{item.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: item.originColor || th.accent, border: `1px solid ${line}`, borderRadius: 99, padding: "1px 8px" }}>{item.origin}</span>
                {mostrarConsultor && item.consultor && <span style={{ fontSize: 12, color: th.muted }}>{item.consultor}</span>}
                {item.passou15h && <span title="O gerente já está vendo este atraso" style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: "#7A1010", borderRadius: 99, padding: "1px 8px" }}>+15h</span>}
              </span>
              <span style={{ display: "block", fontSize: 13, color: th.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.preview}</span>
              <TextSendStatus storeId={item.storeId} phone={item.phone} />
            </span>
            {item.ago && <span style={{ fontFamily: MONO, fontSize: 12, color: "#B42318", flexShrink: 0 }}>há {item.ago}</span>}
          </button>
        ))}
      </Bloco>

      {fila && (
        <Bloco theme={th} titulo="Prospectar hoje" contador={filaItens.length ? `${filaFeitos} de ${filaItens.length}` : "—"} cor={th.accent}
          acao={<button type="button" onClick={onIrProspeccao} style={{ background: "none", border: "none", color: th.accent, fontSize: 13, fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Abrir Prospecção</button>}>
          {fila.carregando && !fila.fila && <Vazio theme={th} texto="Carregando a fila de hoje…" />}
          {fila.erro && <Vazio theme={th} texto={fila.erro} />}
          {mostrarBloqueioData && <Vazio theme={th} texto={`O tombamento começa em ${fila.fila.inicioTombamento || "1º/10/2026"}.`} />}
          {fila.fila && !mostrarBloqueioData && fila.fila.participa === false && <Vazio theme={th} texto="Você não participa do tombamento." />}
          {fila.fila && fila.fila.participa !== false && filaItens.length === 0 && <Vazio theme={th} texto="A fila de hoje ainda não tem clientes." />}
          {filaPendentes.slice(0, 5).map(it => (
            <button key={it.id} type="button" onClick={onIrProspeccao} style={linha}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 700, fontSize: 15 }}>{it.nome}</span>
                <span style={{ display: "block", fontSize: 13, color: th.muted, marginTop: 2 }}>{it.motivo}{it.sobraDe ? " · ficou de ontem" : ""}</span>
              </span>
            </button>
          ))}
          {filaPendentes.length > 5 && <Vazio theme={th} texto={`E mais ${filaPendentes.length - 5} na Prospecção.`} />}
          {filaItens.length > 0 && filaPendentes.length === 0 && <Vazio theme={th} texto="Fila de hoje concluída. 🎉" />}
        </Bloco>
      )}

      <Bloco theme={th} titulo="Retornos de hoje e atrasados" contador={retornos.length} cor="#B45309">
        {retornos.length === 0 && <Vazio theme={th} texto="Nenhum retorno para hoje." />}
        {orderedRetornos.map(r => (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${line}`, padding: "0 16px" }}>
            {r.projectedProspectReturn ? <span aria-label="Retorno vinculado ao cartão">↗</span> : <ReminderCompletionButton reminder={r} onComplete={onConcluirRetorno} label="Marcar retorno como feito" style={{
              width: 22, height: 22, minWidth: 22, borderRadius: 4, border: `2px solid ${th.muted}`, background: "#fff", cursor: "pointer" }} />}
            <button type="button" onClick={() => onAbrirRetorno(r)} style={{ ...linha, borderBottom: "none", padding: "11px 0", flex: 1 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{r.text}</span>
                <TextSendStatus storeId={r.storeId} phone={r.clientPhone || r.phone} onOpen={() => onAbrirRetorno(r)} />
                {r.clientName && <span style={{ display: "block", fontSize: 12, color: th.muted }}>{r.clientName}{mostrarConsultor && r.consultor ? ` · ${r.consultor}` : ""}</span>}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: r.atrasado ? "#B42318" : "#B45309", flexShrink: 0 }}>{r.atrasado ? "Atrasado" : "Hoje"}</span>
            </button>
          </div>
        ))}
      </Bloco>
    </div>
  );
}